import { Component, OnInit, ChangeDetectionStrategy, inject } from "@angular/core";
import { ModalController, IonButton, IonContent, IonFooter, IonHeader, IonIcon, IonLabel, IonSearchbar, IonTitle, IonToolbar } from "@ionic/angular";
import { AppSettings } from "src/components/shared-components/class/general-classes/app-settings";
import { S3UploadService } from "src/components/shared-components/service/aws-upload.service";
import { AlertService } from "src/service/alert.service";
import { LoaderService } from "src/service/loader.service";
import { ThemeService } from "src/service/theme.service";
import { PlatformModeContentDirective } from "../../../../../directive/platform-mode-content.directive";
import { TranslatePipe } from "@ngx-translate/core";

@Component({
    selector: "app-popup-repository-icon",
    templateUrl: "./popup-repository-icon.component.html",
    styleUrls: ["./popup-repository-icon.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [IonButton, IonContent, IonFooter, IonHeader, IonIcon, IonLabel, IonSearchbar, IonTitle, IonToolbar, PlatformModeContentDirective, TranslatePipe]
})
export class PopupRepositoryIconComponent implements OnInit {
  private readonly _uploadFileToS3 = inject(S3UploadService);
  private readonly _loaderService = inject(LoaderService);
  private readonly _modalCtrl = inject(ModalController);
  private readonly _alertService = inject(AlertService);
  private readonly _themeService = inject(ThemeService);

  public listImages;
  private currentIndex = 0;
  private batchSize = 70;
  public isScrollVisible = true;
  public showFolder = false;
  public showSpinner = true;
  public allLoaded = false;
  public listfolders = [];
  public currentFolder = "";
  public listImagesFilteredFolder;
  public currentSearchingText = "";
  public listImagesFiltered: any = {
    files: [],
    folders: [],
  };
  public listAllImages = [];
  public listImagesFolder = [];
  public listImagesAllFolder = [];
  public listAllFolders = [];
  public selectedIconUrl: string = "";

  async ngOnInit() {
    try {
      let onlyOneFolder = false;
      await this._loaderService.presentLoaderWaiting();
      this.listfolders = await this._uploadFileToS3.getFolders(
        this._themeService.applicationTheme,
      );
      this.listAllFolders = this.listfolders;
      this.showFolder = this.listAllFolders.length > 1;
      if (this.listAllFolders.length > 1) {
        this.listAllImages = await this._uploadFileToS3.getFilesByFolder();
      } else {
        onlyOneFolder = true;
        this.listImagesFolder = await this._uploadFileToS3.getFilesByFolder(
          this.listAllFolders[0],
        );
        this.listImagesFilteredFolder = this.listImagesFolder;

        this.currentFolder = this.listAllFolders[0];
      }
      for (const file of this.listImagesFolder) {
        const fullPath = file.key;
        const fileName = fullPath.split("/").pop() || "";
        const parts = fileName.split("_");

        const nameAfterThirdUnderscore = parts.slice(3).join("_");
        file.name = nameAfterThirdUnderscore.replace(".png", "");
      }
      if (onlyOneFolder) {
        this.listImagesAllFolder = [...this.listImagesFolder];
      }
      await this._loaderService.dismissLoaderWaiting();
    } catch {
      await this._loaderService.dismissLoaderWaiting();
      await this._alertService.presentAlertSomethingWrong();
    }
  }

  /**
   * This method is call when the user search something on the search bar
   */
  public filterBySearchText(event) {
    if (!this.showFolder) {
      this.scrollTop();
      this.currentSearchingText = event.detail.value;
      this.filterIconBySearchText();
    } else {
      this.listfolders = this.listAllFolders.filter((item) => {
        return item.toLowerCase().includes(event.detail.value.toLowerCase());
      });
    }
  }

  /**
   * This method is used to scroll to the top of the page
   */
  public scrollTop() {
    const divElement = document.getElementById("scrollable");
    if (divElement) {
      divElement.scrollTop = 0;
    }
  }

  /**
   * Get the url to show the icon
   * @param iconUrl
   * @returns
   */
  public getUrlIcon(iconUrl: string): string {
    return AppSettings.URL_ICONS_REPOSITORY + encodeURIComponent(iconUrl);
  }

  /**
   * Formats the folder name for display by removing "BIG" or "VIMAR" prefixes.
   * @param folder The folder name to format.
   * @returns The formatted folder name.
   */
  public formatFolderName(folder: string): string {
    if (!folder) return "";
    return folder.replace(/(BIG|VIMAR)\/?/gi, "").trim();
  }

  /**
   * This method is used to filter the images by color and search text
   */
  public filterIconBySearchText() {
    this.currentIndex = 0;

    this.listImagesFilteredFolder = [
      ...this.listImagesAllFolder.filter((item) => {
        const textMatch = this.currentSearchingText
          ? item["name"]
              .toLowerCase()
              .includes(this.currentSearchingText.toLowerCase())
          : true;

        return textMatch;
      }),
    ];

    this.listImagesFolder = [...this.listImagesFilteredFolder].slice(
      0,
      this.batchSize,
    );
    this.currentIndex += this.batchSize;
  }
  /**
   * Click back on list folder
   */
  public onClickBack() {
    this.showFolder = true;
    this.listImagesFolder = [];
    this.listImagesAllFolder = [];
    this.listImagesFilteredFolder = [];
    this.currentFolder = "";
  }

  /**
   * Show the icon in the folder
   * @param folder
   */
  public onClickOpenFolder(folder): void {
    this.listImagesAllFolder = [
      ...this.listAllImages.filter(
        (item) => item.folder === folder && item.key != folder + "/",
      ),
    ];
    this.listImagesFolder = [...this.listImagesAllFolder].slice(
      0,
      this.batchSize,
    );
    this.listImagesFilteredFolder = this.listImagesAllFolder;
    this.currentFolder = folder;
    this.showFolder = false;
  }

  /**
   * This method is used to load more data when scrolling
   */
  public onScrollLoadMoreData(event): void {
    setTimeout(() => {
      const nextBatch = this.listImagesFilteredFolder.slice(
        this.currentIndex,
        this.currentIndex + this.batchSize,
      );
      if (nextBatch.length === 0) {
        this.showSpinner = false;
        this.allLoaded = true;
      } else {
        this.listImagesFolder = [...this.listImagesFolder, ...nextBatch];
        this.currentIndex += this.batchSize;
      }
    }, 1000);
  }

  /**
   * Handle click on icon
   * @param url url of the icon
   */
  public onClickSelectIcon(url: string) {
    this.selectedIconUrl = url;
  }

  /**
   * Handle choice when user clicks ok in modal
   */
  public onClickConfirmChoice(): void {
    this._modalCtrl.dismiss({
      selectedIcon: this.selectedIconUrl,
    });
  }

  /**
   * Dismiss modal
   */
  public onClickDismiss(): void {
    this._modalCtrl.dismiss();
  }
}
